import React, { useEffect, useState } from 'react'
import {
  Button,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material'
import {
  EditBase,
  Form,
  TextInput,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'react-admin'
import { validateAutoSaveSingleInput } from '../../../../Utils/AutoSave'
import {
  calculateCommission,
  calculatePrices,
  getAdminUsers,
} from '../../../../Utils/RestUtils'
import RetailAndManualInputs from './RetailAndManualFields'
import CapFields from './CapFields'
import AutotraderFields from './AutotraderFields'
import './style/index.scss'
import { blockInvalidCharFromNumberInput } from '../../../../Utils/ConversionUtils'
import { calculateAdjustmentPrice } from '../../../../Utils/PricingUtils'
import GetOffersButton from './GetOffersButton'
import CapValueField from './CapValueField'

const CapPanelML = () => {
  const record = useRecordContext()
  const notify = useNotify()
  const refresh = useRefresh()
  const [adminUsers, setAdminUsers] = useState()
  const [isLoadingCalculate, setIsLoadingCalculate] = useState(false)
  const [mileageEstimate, setMileageEstimate] = useState(null)
  const [approvedBy, setApprovedBy] = useState()
  const [recalculate, setRecalculate] = useState(
    window.localStorage.getItem('recalculate')
  )
  const roles = localStorage.getItem('permissions').split(',')
  const user_id = localStorage.getItem('user_id')
  const allowedUsers = [12, 13, 45, 134].includes(Number(user_id))

  const { leadContact, latestPricingPrediction, status, dateCreated } = record
  const { vehicle } = leadContact
  const { vehicleSalvage } = vehicle
  const hasOfferJourney = vehicleSalvage?.offerJourneyFollowed === true

  useEffect(() => {
    let isMounted = true

    const getSelectOptions = async () => {
      const admins = await getAdminUsers()

      if (isMounted) {
        setAdminUsers(admins)
      }
    }

    const handleStorage = () => {
      setRecalculate(window.localStorage.getItem('recalculate'))
    }

    getSelectOptions()
    window.addEventListener('storage', handleStorage)

    return () => {
      isMounted = false
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  useEffect(() => {
    setApprovedBy(vehicle?.vehicleSalvage?.approvedBy?.['@id'])
    setMileageEstimate(vehicle?.mileageEstimate ?? null)
  }, [vehicle])

  const handleEstimateChange = async (e) => {
    setMileageEstimate(e.target.value)

    await validateAutoSaveSingleInput(
      record,
      e.target,
      notify,
      refresh,
      'Mileage Estimated'
    )
  }

  const handleApprovedByChange = async (e) => {
    setApprovedBy(e.target.value)

    await validateAutoSaveSingleInput(
      record,
      e.target,
      notify,
      refresh,
      'Approved By'
    )
  }

  const saveChosenValue = async (
    props,
    inputEvent,
    notify,
    refresh,
    inputPopupText = ''
  ) => {
    await validateAutoSaveSingleInput(
      record,
      inputEvent,
      notify,
      refresh,
      inputPopupText
    )

    await calculateCommission({
      vehicle: record.leadContact.vehicle['@id'],
      recalculate: true,
    })

    refresh()
  }

  return (
    <EditBase>
      <Form>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: 2,
          }}
          className="cap-panel"
        >
          <Box
            sx={{
              gridRow: 'span 2',
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'stretch',
            }}
          >
            <GetOffersButton />
            <Box sx={{ mt: 'auto' }}>
              <CapValueField />
            </Box>
          </Box>

          <TextField
            label="Target price"
            disabled
            value={
              hasOfferJourney &&
              latestPricingPrediction &&
              latestPricingPrediction.targetPrice
                ? latestPricingPrediction.targetPrice.toFixed(2) ?? ''
                : ''
            }
          />

          <Box>
            <TextInput
              label="Mileage"
              type="number"
              className={
                !mileageEstimate ? 'mileage-confirm' : 'estimated-mileage'
              }
              disabled={!!mileageEstimate}
              inputProps={{
                step: 1,
                min: 0,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              onKeyDown={blockInvalidCharFromNumberInput}
              source="leadContact.vehicle.mileage"
              onBlur={async (e) =>
                await validateAutoSaveSingleInput(
                  record,
                  e.target,
                  notify,
                  refresh
                )
              }
            />
          </Box>
          <Box>
            <FormControl sx={{ width: '100%' }} size="small">
              <InputLabel id="est-mileage-label" shrink>
                Est. Mileage?
              </InputLabel>
              <Select
                labelId="est-mileage-label"
                label="Est. Mileage?"
                name="leadContact.vehicle.mileageEstimate"
                value={mileageEstimate ?? ''}
                onChange={handleEstimateChange}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Est. Mileage?
                </MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <TextInput
              label="Customer wants"
              type="number"
              inputProps={{
                step: 0.01,
                min: 0,
              }}
              onKeyDown={blockInvalidCharFromNumberInput}
              disabled={!hasOfferJourney}
              source={
                hasOfferJourney
                  ? 'leadContact.vehicle.vehicleSalvage.customerWants'
                  : ''
              }
              onBlur={async (e) =>
                await validateAutoSaveSingleInput(
                  record,
                  e.target,
                  notify,
                  refresh,
                  'Customer Wants'
                )
              }
            />
          </Box>

          <RetailAndManualInputs />

          <Box>
            <FormControl sx={{ width: '100%' }} size="small">
              <InputLabel id="approved-by-label">Approved by</InputLabel>
              <Select
                labelId="approved-by-label"
                name="leadContact.vehicle.vehicleSalvage.approvedBy"
                defaultValue="default"
                label="Approved by"
                value={
                  vehicle.vehicleSalvage && approvedBy !== null
                    ? approvedBy
                    : 'default'
                }
                onChange={handleApprovedByChange}
              >
                <MenuItem disabled value="default">
                  Approved By?
                </MenuItem>
                {adminUsers &&
                  adminUsers.map((user) => {
                    return (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    )
                  })}
              </Select>
            </FormControl>
          </Box>

          <Box>
            {hasOfferJourney &&
              latestPricingPrediction &&
              latestPricingPrediction.guidePrice && (
                <Tooltip
                  title={
                    roles.includes('ROLE_ADMIN') ? (
                      <div
                        key="foxcub-guide-price"
                        style={{ fontSize: '12px', padding: '2px' }}
                      >
                        <div style={{ width: '60px', display: 'inline-block' }}>
                          Foxcub:
                        </div>
                        <div style={{ display: 'inline-block' }}>
                          {vehicleSalvage.low40Value}
                        </div>
                      </div>
                    ) : (
                      ''
                    )
                  }
                >
                  <TextField
                    label="Guide price"
                    disabled
                    value={
                      record &&
                      latestPricingPrediction &&
                      latestPricingPrediction.guidePrice != null &&
                      hasOfferJourney
                        ? Number(latestPricingPrediction.guidePrice).toFixed(2)
                        : ''
                    }
                  />
                </Tooltip>
              )}
          </Box>
          <Box>
            {hasOfferJourney &&
              latestPricingPrediction &&
              latestPricingPrediction.maxPrice && (
                <Tooltip
                  title={
                    roles.includes('ROLE_ADMIN') ? (
                      <div
                        key={'foxcub-max-price'}
                        style={{ fontSize: '12px', padding: '2px' }}
                      >
                        <div style={{ width: '60px', display: 'inline-block' }}>
                          Foxcub:
                        </div>
                        <div style={{ display: 'inline-block' }}>
                          {vehicleSalvage.highValue}
                        </div>
                      </div>
                    ) : (
                      ''
                    )
                  }
                >
                  <TextField
                    label="Max price"
                    disabled
                    value={
                      record &&
                      latestPricingPrediction &&
                      latestPricingPrediction.maxPrice != null &&
                      hasOfferJourney
                        ? Number(latestPricingPrediction.maxPrice).toFixed(2)
                        : ''
                    }
                  />
                </Tooltip>
              )}
          </Box>
          <Box>
            <TextInput
              label="Chosen value £"
              type={'number'}
              inputProps={{
                step: 0.01,
                min: 0,
              }}
              disabled={
                (record.status === 'Collected' &&
                  !roles.includes('ROLE_ADMIN') &&
                  !allowedUsers) ||
                hasOfferJourney
                  ? false
                  : true
              }
              onKeyDown={blockInvalidCharFromNumberInput}
              source={
                hasOfferJourney
                  ? 'leadContact.vehicle.vehicleSalvage.chosenValue'
                  : ''
              }
              onBlur={async (e) =>
                await saveChosenValue(
                  record,
                  e.target,
                  notify,
                  refresh,
                  'Chosen Value'
                )
              }
            />
          </Box>

          <AutotraderFields />
        </Box>
      </Form>
    </EditBase>
  )
}

export default CapPanelML
